/* Lebs Beldi — JS
   Geen frameworks, geen bundler nodig voor Shopify themes.
   Modules: CartDrawer, NavMobile, ProductGallery, SizeSelector, AnnounceBar.
   ================================================================== */

'use strict';

/* ---------- Cart Drawer ------------------------------------------ */
class CartDrawer {
  constructor() {
    this.drawer   = document.getElementById('lb-cart');
    this.scrim    = document.getElementById('lb-cart-scrim');
    this.counter  = document.querySelectorAll('[data-cart-count]');
    this.body     = document.getElementById('lb-cart-body');
    this.subtotal = document.getElementById('lb-cart-subtotal');

    if (!this.drawer) return;

    document.querySelectorAll('[data-open-cart]').forEach(btn =>
      btn.addEventListener('click', () => this.open())
    );
    this.scrim.addEventListener('click', () => this.close());
    document.getElementById('lb-cart-close')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });

    document.addEventListener('lb:cart:change', () => this.refresh());

    /* Event delegation voor verwijder-knoppen (ook server-gerenderd) */
    this.body?.addEventListener('click', e => {
      const btn = e.target.closest('.lb-cart__item-remove');
      if (btn) this.removeItem(btn.dataset.line);
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    this.scrim.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.drawer.setAttribute('aria-hidden', 'false');
    /* Altijd verversen zodat footer en items actueel zijn */
    this.refresh();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.scrim.classList.remove('is-open');
    document.body.style.overflow = '';
    this.drawer.setAttribute('aria-hidden', 'true');
  }

  refresh() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        this.updateCount(cart.item_count);
        this.renderItems(cart);
      })
      .catch(console.error);
  }

  updateCount(count) {
    this.counter.forEach(el => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  renderItems(cart) {
    if (!this.body) return;

    if (cart.item_count === 0) {
      this.body.innerHTML = `
        <div style="text-align:center;padding-top:60px">
          <div class="lb-stars">✦  ✦  ✦</div>
          <p class="lb-body" style="margin-top:20px">
            Je winkelmand is leeg.<br>Begin met de nieuwe collectie.
          </p>
        </div>`;
      if (this.subtotal) this.subtotal.closest('.lb-cart__foot').hidden = true;
      return;
    }

    if (this.subtotal) {
      const foot = this.subtotal.closest('.lb-cart__foot');
      if (foot) foot.hidden = false;
    }

    this.body.innerHTML = cart.items.map(item => `
      <div class="lb-cart__item">
        <div class="lb-cart__item-img">
          ${item.image
            ? `<img src="${item.image}" alt="${item.title}" loading="lazy">`
            : `<div class="lb-cart__item-img-placeholder">${item.title.split(' ').pop()}</div>`
          }
        </div>
        <div>
          <p class="lb-tile__name" style="font-size:16px;margin-bottom:2px">${item.product_title}</p>
          <p class="lb-tile__sub" style="font-size:12px;margin-bottom:8px">Maat ${item.variant_title || ''}</p>
          <button class="lb-cart__item-remove"
            data-line="${item.key}"
            aria-label="Verwijder ${item.product_title} uit winkelmand">
            ${window.lbT?.remove || 'Verwijder'}
          </button>
        </div>
        <span class="lb-caps" style="color:var(--lb-bruin)">
          € ${(item.line_price / 100).toFixed(0)}
        </span>
      </div>`).join('');

    if (this.subtotal) {
      this.subtotal.textContent = `€ ${(cart.total_price / 100).toFixed(2).replace('.', ',')}`;
    }
  }

  removeItem(key) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
    .then(r => r.json())
    .then(cart => {
      this.updateCount(cart.item_count);
      this.renderItems(cart);
    })
    .catch(console.error);
  }
}

/* ---------- Add to cart form ------------------------------------ */
function initAddToCart() {
  document.querySelectorAll('[data-add-to-cart-form]').forEach(form => {
    form.addEventListener('submit', e => {
      e.preventDefault();
      const btn = form.querySelector('[data-add-btn]');
      const originalText = btn.textContent;
      btn.textContent = btn.dataset.addingText || 'Toevoegen…';
      btn.disabled = true;

      const formData = new FormData(form);
      const data = { items: [{ id: Number(formData.get('id')), quantity: 1 }] };

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      })
      .then(r => r.json())
      .then(() => {
        document.dispatchEvent(new Event('lb:cart:change'));
        document.querySelector('[data-open-cart]')?.click();
      })
      .catch(console.error)
      .finally(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      });
    });
  });
}

/* ---------- Size selector --------------------------------------- */
function initSizeSelector() {
  document.querySelectorAll('.lb-size-group').forEach(group => {
    const hidden = group.querySelector('[data-variant-id]');
    group.querySelectorAll('.lb-size').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-sold-out')) return;
        group.querySelectorAll('.lb-size').forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        if (hidden) hidden.value = btn.dataset.variantId;
      });
    });
  });
}

/* ---------- Product gallery thumbs ----------------------------- */
function initGallery() {
  document.querySelectorAll('.lb-pdp-thumbs').forEach(strip => {
    const mainImg = strip.closest('.lb-pdp-gallery')?.querySelector('.lb-pdp-main-img img');
    strip.querySelectorAll('.lb-pdp-thumb').forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        strip.querySelectorAll('.lb-pdp-thumb').forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        if (mainImg) {
          const src = thumb.querySelector('img')?.src;
          if (src) mainImg.src = src;
        }
      });
    });
  });
}

/* ---------- Mobile nav ------------------------------------------ */
function initMobileNav() {
  const btn   = document.querySelector('.lb-nav__hamburger');
  const links = document.querySelector('.lb-nav__links');
  if (!btn || !links) return;

  btn.addEventListener('click', () => {
    const open = links.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', String(open));
  });
}

/* ---------- Filter active state (PLP) --------------------------- */
function initFilters() {
  document.querySelectorAll('.lb-filter-form').forEach(form => {
    form.querySelectorAll('input[type="checkbox"], select').forEach(input => {
      input.addEventListener('change', () => form.submit());
    });
  });
}

/* ---------- Boot ------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  new CartDrawer();
  initAddToCart();
  initSizeSelector();
  initGallery();
  initMobileNav();
  initFilters();
});
